import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../../services/api';
import { Carregando, EstadoVazio, Selo } from '../../../common/interface';
import type { Papel } from '../../../common/interface';
import { dataDoDia, moeda } from '../../formato';
import { origemMensalidade } from '../../usePixCobranca';
import { PixDaCobranca } from './PixFaturaSheet';
import type { PixResumo } from './types';

/** Contrato de `GET /api/cooperado/mensalidades` (spec 0105). */
interface Mensalidade {
  id: number;
  idMotorista: number;
  nomeMotorista: string | null;
  competencia: string;
  competenciaRotulo: string;
  valorDevido: number;
  valorDescontado: number;
  valor: number;
  motivoCobranca: 'FORA_DO_FECHAMENTO' | 'SEM_APURACAO' | 'DESCONTO_PARCIAL';
  motivoRotulo: string;
  modo: 'individual' | 'grupo';
  dtVencimento: string;
  status: 'PENDENTE' | 'PAGA' | 'CANCELADA';
  vencida: boolean;
  dtPagamento: string | null;
  formaPagamento: 'PIX' | 'MANUAL' | null;
  pix: PixResumo | null;
}

const situacao = (m: Mensalidade): { nome: string; papel: Papel } =>
  m.status === 'PAGA' ? { nome: 'Pago', papel: 'sucesso' }
    : m.status === 'CANCELADA' ? { nome: 'Cancelada', papel: 'neutro' }
      : m.vencida ? { nome: 'Vencida', papel: 'perigo' }
        : { nome: 'Aguardando pagamento', papel: 'atencao' };

/**
 * As mensalidades do cooperado (spec 0105).
 *
 * A mensalidade é descontada do repasse; aqui aparece só o que o repasse não
 * descontou. As canceladas não entram: para quem paga, uma mensalidade
 * cancelada é uma que não deve.
 */
export function MensalidadesLista({ onTotal }: { onTotal: (emAberto: number) => void }) {
  const [itens, setItens] = useState<Mensalidade[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [pagando, setPagando] = useState<Mensalidade | null>(null);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const r = await api.get<{ data: Mensalidade[] }>('/cooperado/mensalidades', { params: { limit: 50 } });
      setItens(r.data.data.filter((m) => m.status !== 'CANCELADA'));
    } catch {
      setErro('Não foi possível carregar suas mensalidades. Verifique a conexão e tente novamente.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { onTotal(itens.filter((m) => m.status === 'PENDENTE').length); }, [itens, onTotal]);

  const origem = useMemo(() => (pagando ? origemMensalidade(pagando.id) : null), [pagando?.id]);
  const deVariasPessoas = new Set(itens.map((m) => m.idMotorista)).size > 1;

  return (
    <>
      <p className="co-nota co-nota-info">
        A mensalidade é descontada do seu repasse. Quando o repasse do mês não tem de onde descontar, ela aparece
        aqui para você pagar por Pix.
      </p>

      {erro && (
        <div className="co-erro" role="alert">
          {erro}
          <div className="co-acoes">
            <button type="button" className="co-btn co-btn-secundario" onClick={carregar}>Tentar de novo</button>
          </div>
        </div>
      )}

      {carregando && <Carregando rotulo="Carregando as mensalidades" />}

      {!carregando && !erro && itens.length === 0 && (
        <EstadoVazio titulo="Nenhuma mensalidade para pagar">
          Sua mensalidade foi descontada do repasse. Se um mês não tiver de onde descontar, ela aparece aqui.
        </EstadoVazio>
      )}

      {itens.map((m) => {
        const s = situacao(m);
        const emAberto = m.status === 'PENDENTE';
        return (
          <article key={m.id} className="co-cartao co-fatura">
            <div className="co-fatura-cabeca">
              <div>
                <div className="co-titulo-cartao">{m.competenciaRotulo}</div>
                {deVariasPessoas && m.nomeMotorista && <div className="co-apoio">{m.nomeMotorista}</div>}
                {m.modo === 'grupo' && <div className="co-apoio">Mensalidade do grupo</div>}
              </div>
              <Selo papel={s.papel}>{s.nome}</Selo>
            </div>

            <dl className="co-pares">
              {m.status === 'PAGA' ? (
                <>
                  <dt>Pago em</dt>
                  <dd>{dataDoDia(m.dtPagamento)}</dd>
                </>
              ) : (
                <>
                  <dt>Vencimento</dt>
                  <dd>{dataDoDia(m.dtVencimento)}</dd>
                </>
              )}
              <dt>Por quê</dt>
              <dd>{m.motivoRotulo}</dd>
              <dt>Valor</dt>
              <dd className="co-fatura-valor">{moeda(m.valor)}</dd>
            </dl>

            {m.valorDescontado > 0 && (
              <p className="co-nota">
                {moeda(m.valor)} de {moeda(m.valorDevido)}. {moeda(m.valorDescontado)} já foram descontados no
                fechamento de {m.competencia}.
              </p>
            )}

            {emAberto && (
              <div className="co-acoes">
                <button type="button" className="co-btn co-btn-primario co-btn-largo" onClick={() => setPagando(m)}>
                  {m.pix ? 'Ver código Pix' : 'Pagar com Pix'}
                </button>
              </div>
            )}
          </article>
        );
      })}

      <PixDaCobranca
        origem={origem}
        contexto={pagando ? `Mensalidade de ${pagando.competencia} · vence ${dataDoDia(pagando.dtVencimento)}` : undefined}
        valor={pagando?.valor ?? null}
        oQueBaixa="mensalidade"
        onFechar={() => setPagando(null)}
        onDesfecho={carregar}
      />
    </>
  );
}
